/**
 * session-result-store.ts — local persistence for session results (Phase 7
 * scoring, task #4).
 *
 * A `SessionResultStore` keeps the most recent practice results so the console
 * panel (#5) can show a history. The store is split from its backing storage:
 *
 *   - `KeyValueStore` is the injected IO seam — async getItem/setItem/removeItem.
 *     Web injects a `localStorage` adapter (see `web-session-store.ts`); RN can
 *     inject an `AsyncStorage` adapter with the same shape. Tests inject the
 *     in-memory adapter here.
 *   - `createSessionResultStore(kv, opts)` is storage-agnostic: serialise, parse
 *     defensively, prepend newest-first, roll to a hard cap of N entries.
 *
 * Invariants (GOAL.md): only the summary `SessionResult` is stored — never raw
 * PCM or any audio/sensitive payload. The record carries metadata the caller
 * supplies (id/savedAtMs); the store never reads the clock or RNG, so it stays
 * deterministic given its inputs.
 */

import type { SessionResult } from './summarize-session';

/** Async key/value seam. localStorage (sync) and AsyncStorage (async) both fit. */
export interface KeyValueStore {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
}

/** One persisted practice result. Summary only — no raw audio. */
export type SessionRecord = {
  /** Caller-supplied unique id (e.g. a uuid). */
  id: string;
  /** When the session was saved, ms since epoch (caller-supplied). */
  savedAtMs: number;
  /** Target melody this session was scored against. */
  melodyId: string;
  /** Display title (melody title at save time). */
  title: string;
  /** Pure summary from `summarizeSession`. */
  result: SessionResult;
};

export interface SessionResultStore {
  /** Persist a record (prepended, newest first, rolled to the cap). */
  save(record: SessionRecord): Promise<void>;
  /** All stored records, newest first. */
  list(): Promise<SessionRecord[]>;
  /** One record by id, or null. */
  get(id: string): Promise<SessionRecord | null>;
  /** Remove every stored record. */
  clear(): Promise<void>;
}

export type SessionResultStoreOptions = {
  /** Backing key. Default `muzly.scoring.sessionResults`. */
  storageKey: string;
  /** Hard cap on retained records; oldest beyond this are dropped. */
  maxEntries: number;
};

export const DEFAULT_SESSION_STORE_OPTIONS: SessionResultStoreOptions = {
  storageKey: 'muzly.scoring.sessionResults',
  maxEntries: 20,
};

// ── defensive parsing ─────────────────────────────────────────────────────────

/** A persisted record must at least carry the identity fields we index on. */
const isSessionRecord = (value: unknown): value is SessionRecord => {
  if (typeof value !== 'object' || value === null) return false;
  const r = value as Record<string, unknown>;
  return (
    typeof r.id === 'string' &&
    typeof r.savedAtMs === 'number' &&
    typeof r.melodyId === 'string' &&
    typeof r.title === 'string' &&
    typeof r.result === 'object' &&
    r.result !== null
  );
};

/** Parse the stored blob, dropping anything malformed rather than throwing. */
const parseRecords = (raw: string | null): SessionRecord[] => {
  if (!raw) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  return parsed.filter(isSessionRecord);
};

// ── factory ───────────────────────────────────────────────────────────────────

export const createSessionResultStore = (
  kv: KeyValueStore,
  opts: SessionResultStoreOptions = DEFAULT_SESSION_STORE_OPTIONS,
): SessionResultStore => {
  const { storageKey, maxEntries } = opts;

  const read = async (): Promise<SessionRecord[]> =>
    parseRecords(await kv.getItem(storageKey));

  const write = async (records: SessionRecord[]): Promise<void> => {
    await kv.setItem(storageKey, JSON.stringify(records));
  };

  return {
    save: async (record) => {
      const existing = (await read()).filter((r) => r.id !== record.id);
      const next = [record, ...existing].slice(0, Math.max(0, maxEntries));
      await write(next);
    },
    list: async () => read(),
    get: async (id) => (await read()).find((r) => r.id === id) ?? null,
    clear: async () => {
      await kv.removeItem(storageKey);
    },
  };
};

// ── in-memory adapter (tests, and a safe no-DOM fallback) ──────────────────────

export const createMemoryKeyValueStore = (): KeyValueStore => {
  const map = new Map<string, string>();
  return {
    getItem: (key) => Promise.resolve(map.get(key) ?? null),
    setItem: (key, value) => {
      map.set(key, value);
      return Promise.resolve();
    },
    removeItem: (key) => {
      map.delete(key);
      return Promise.resolve();
    },
  };
};
